import { IEvent } from "@Interfaces/redux/store/IEvent";
import { IHost } from "@Interfaces/redux/store/IHost";
import { getBaseFormFields } from "@Modules/modals/components/UserDetails/UserDetails.Utils";
import {
    GST_STATUS,
    QUESTION_PLACEMENT,
} from "repoV2/features/Listing/Listing.constants";
import { checkIsArrayEmpty } from "repoV2/utils/common/dataTypes/array";
import { getAreAllUserDetailsFieldsAllowedForDirectBooking } from "./getAreAllUserDetailsFieldsAllowedForDirectBooking";
import { getBookingQuestions } from "./getBookingQuestions";
import { getIsOfferingSupportsDirectBooking } from "./getIsDirectBookingListing";

/**
 * Determines whether the user details form can be skipped for booking.
 * @returns {boolean} - True if the user details form can be skipped, otherwise false.
 */
export const getCanSkipUserDetailsForm = ({
    hostData,
    eventData,
    showGstOption,
    isValidMerchandiseOffering,
    hasToShowFullAddress,
    numberOfCustomers,
}: {
    hostData: IHost.IHostData;
    eventData: IEvent.IEventData;
    showGstOption?: boolean;
    isValidMerchandiseOffering: boolean;
    hasToShowFullAddress?: boolean;
    numberOfCustomers: number;
}) => {
    if (!getIsOfferingSupportsDirectBooking()) return false;

    const isInternationalCreator = !!hostData?.is_international_creator;

    // Check if gstState is not inapplicable
    const gstState = eventData?.gst_state;
    const isGSTStateNotInapplicable =
        gstState === GST_STATUS.INCLUSIVE || gstState === GST_STATUS.EXCLUSIVE;

    const filteredQuestions = getBookingQuestions({
        eventData,
        hostData,
        placement: QUESTION_PLACEMENT.BEFORE_BOOKING,
    });

    // Whether to show the custom questions on the same page or not
    const askQuestionsWhileBooking: boolean = !!(
        !checkIsArrayEmpty(filteredQuestions) ?? false
    );

    const userDetailsBaseFormFields: Array<string> = getBaseFormFields({
        isBooking: true,
        isValidMerchandise: isValidMerchandiseOffering,
        detailsCount: 1,
        hasToShowFullAddress,
        isInternationalCreator,
        isGSTStateNotInapplicable,
        currency: eventData?.currency_name,
    });

    // checking if there are any additional fields in the form aside from name, email, and phone number.
    const needToFillForm =
        numberOfCustomers > 1 ||
        askQuestionsWhileBooking ||
        showGstOption ||
        !getAreAllUserDetailsFieldsAllowedForDirectBooking(
            userDetailsBaseFormFields
        );

    return !needToFillForm;
};
